import React, { useState, useMemo } from 'react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import {
  ChevronLeft, ChevronRight, Calendar as CalIcon, Clock, User, Car,
  AlertTriangle, CheckCircle, Wrench
} from 'lucide-react';

const STATUS_CONFIG = {
  beklemede: { label: 'Beklemede', color: '#6b7280', bg: 'bg-gray-600' },
  randevu_planlandi: { label: 'Randevu Planlandı', color: '#6366f1', bg: 'bg-indigo-600' },
  arac_kabul: { label: 'Araç Kabul', color: '#0891b2', bg: 'bg-cyan-600' },
  islemde: { label: 'İşlemde', color: '#3b82f6', bg: 'bg-blue-600' },
  kalite_kontrol: { label: 'Kalite Kontrol', color: '#ca8a04', bg: 'bg-yellow-600' },
  teslime_hazir: { label: 'Teslime Hazır', color: '#22c55e', bg: 'bg-green-600' },
  teslim_edildi: { label: 'Teslim Edildi', color: '#374151', bg: 'bg-gray-700' },
  pending: { label: 'Beklemede', color: '#6b7280', bg: 'bg-gray-600' },
  in_progress: { label: 'İşlemde', color: '#3b82f6', bg: 'bg-blue-600' },
  ready: { label: 'Teslime Hazır', color: '#22c55e', bg: 'bg-green-600' },
  completed: { label: 'Teslim Edildi', color: '#374151', bg: 'bg-gray-700' },
};

const getS = (status) => STATUS_CONFIG[status] || STATUS_CONFIG.beklemede;

const DAYS_TR = ['Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt', 'Paz'];
const MONTHS_TR = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];

function startOfWeekMon(d) {
  const day = d.getDay();
  const diff = (day === 0 ? -6 : 1) - day;
  const r = new Date(d);
  r.setDate(r.getDate() + diff);
  r.setHours(0, 0, 0, 0);
  return r;
}

function isSameDay(a, b) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function toDateStr(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function parseDate(s) {
  if (!s) return null;
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
}

const OperationalCalendar = ({ jobs = [], staffList = [], onEditJob, currentUserId }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('month');
  const [selectedJob, setSelectedJob] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterStaff, setFilterStaff] = useState('all');
  const [filterService, setFilterService] = useState('all');

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const todayStr = toDateStr(today);

  // Collect unique services
  const allServices = useMemo(() => {
    const set = new Set();
    jobs.forEach(j => (j.services || []).forEach(s => set.add(s)));
    return [...set].sort();
  }, [jobs]);

  // Filter jobs
  const filteredJobs = useMemo(() => {
    return jobs.filter(j => {
      if (filterStatus !== 'all' && j.status !== filterStatus) return false;
      if (filterStaff !== 'all' && j.salesId !== filterStaff && j.technicianId !== filterStaff) return false;
      if (filterService !== 'all' && !(j.services || []).includes(filterService)) return false;
      return true;
    });
  }, [jobs, filterStatus, filterStaff, filterService]);

  // Map jobs to date keys using arrivalDate + targetCompletionDate
  const jobsByDate = useMemo(() => {
    const map = {};
    filteredJobs.forEach(j => {
      const arrival = j.arrivalDate || j.startDate?.split('T')[0] || j.createdAt?.split('T')[0];
      const target = j.targetCompletionDate;
      const dates = new Set();
      if (arrival) dates.add(arrival);
      if (target && target !== arrival) dates.add(target);
      dates.forEach(d => {
        if (!map[d]) map[d] = [];
        map[d].push({ ...j, _dateType: d === arrival ? 'arrival' : 'target' });
      });
    });
    return map;
  }, [filteredJobs]);

  // Navigation
  const nav = (dir) => {
    const d = new Date(currentDate);
    if (viewMode === 'month') d.setMonth(d.getMonth() + dir);
    else d.setDate(d.getDate() + dir * 7);
    setCurrentDate(d);
  };

  // Generate calendar days
  const calendarDays = useMemo(() => {
    if (viewMode === 'month') {
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth();
      const first = new Date(year, month, 1);
      const last = new Date(year, month + 1, 0);
      const startDay = startOfWeekMon(first);
      const days = [];
      const d = new Date(startDay);
      while (d <= last || days.length % 7 !== 0 || days.length < 35) {
        days.push(new Date(d));
        d.setDate(d.getDate() + 1);
        if (days.length > 42) break;
      }
      return days;
    } else {
      const start = startOfWeekMon(currentDate);
      return Array.from({ length: 7 }, (_, i) => {
        const d = new Date(start);
        d.setDate(d.getDate() + i);
        return d;
      });
    }
  }, [currentDate, viewMode]);

  // Job chip on calendar
  const JobChip = ({ job }) => {
    const cfg = getS(job.status);
    const isOverdue = job.targetCompletionDate && parseDate(job.targetCompletionDate) < today
      && !['teslim_edildi', 'completed'].includes(job.status);
    const isDueToday = job.targetCompletionDate === todayStr && !['teslim_edildi', 'completed'].includes(job.status);
    const isReady = ['teslime_hazir', 'ready'].includes(job.status);
    const isMine = currentUserId && (job.technicianId === currentUserId || job.salesId === currentUserId);

    return (
      <button
        onClick={(e) => { e.stopPropagation(); setSelectedJob(job); }}
        className={`w-full text-left text-[10px] leading-tight px-1.5 py-0.5 rounded mb-0.5 truncate transition-all hover:brightness-125 border-l-2 ${
          isOverdue ? 'bg-red-900/60 border-red-500' :
          isDueToday ? 'bg-amber-900/50 border-amber-400' :
          isReady ? 'bg-green-900/50 border-green-500' :
          isMine ? 'bg-brand/40 border-brand' :
          'bg-gray-800/80 border-transparent'
        }`}
        style={{ borderLeftColor: isOverdue ? '#ef4444' : isDueToday ? '#f59e0b' : isReady ? '#22c55e' : isMine ? '#f97316' : cfg.color }}
        data-testid={`cal-job-${job.id}`}
      >
        <span className="font-semibold text-white">{job.carPlate}</span>
        {isMine && <span className="text-brand ml-0.5 text-[8px]">*</span>}
        {job._dateType === 'target' && <span className="text-brand ml-1">T</span>}
      </button>
    );
  };

  return (
    <div data-testid="operational-calendar">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={() => nav(-1)}
            className="border-gray-700 text-gray-300 h-8 w-8 p-0"><ChevronLeft className="h-4 w-4" /></Button>
          <h3 className="text-white font-semibold text-sm min-w-[140px] text-center">
            {viewMode === 'month'
              ? `${MONTHS_TR[currentDate.getMonth()]} ${currentDate.getFullYear()}`
              : `${calendarDays[0]?.getDate()} - ${calendarDays[6]?.getDate()} ${MONTHS_TR[currentDate.getMonth()]} ${currentDate.getFullYear()}`
            }
          </h3>
          <Button size="sm" variant="outline" onClick={() => nav(1)}
            className="border-gray-700 text-gray-300 h-8 w-8 p-0"><ChevronRight className="h-4 w-4" /></Button>
          <Button size="sm" variant="outline" onClick={() => setCurrentDate(new Date())}
            className="border-gray-700 text-gray-300 h-8 text-xs px-2">Bugün</Button>
        </div>

        <div className="flex items-center gap-2">
          {/* View toggle */}
          <div className="flex bg-gray-800 rounded-md border border-gray-700">
            <button onClick={() => setViewMode('month')}
              className={`px-3 py-1 text-xs rounded-l-md ${viewMode === 'month' ? 'bg-brand text-white' : 'text-gray-400'}`}
              data-testid="cal-month-btn">Ay</button>
            <button onClick={() => setViewMode('week')}
              className={`px-3 py-1 text-xs rounded-r-md ${viewMode === 'week' ? 'bg-brand text-white' : 'text-gray-400'}`}
              data-testid="cal-week-btn">Hafta</button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 mb-3">
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="bg-gray-800 border-gray-700 text-white h-8 text-xs w-[150px]" data-testid="cal-filter-status">
            <SelectValue placeholder="Durum" />
          </SelectTrigger>
          <SelectContent className="bg-gray-900 border-gray-700">
            <SelectItem value="all" className="text-white text-xs">Tüm Durumlar</SelectItem>
            {Object.entries(STATUS_CONFIG).filter(([k]) => !['pending','in_progress','ready','completed'].includes(k)).map(([k, v]) => (
              <SelectItem key={k} value={k} className="text-white text-xs">{v.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filterStaff} onValueChange={setFilterStaff}>
          <SelectTrigger className="bg-gray-800 border-gray-700 text-white h-8 text-xs w-[150px]" data-testid="cal-filter-staff">
            <SelectValue placeholder="Personel" />
          </SelectTrigger>
          <SelectContent className="bg-gray-900 border-gray-700">
            <SelectItem value="all" className="text-white text-xs">Tüm Personel</SelectItem>
            {staffList.map(s => <SelectItem key={s.id} value={s.id} className="text-white text-xs">{s.name}</SelectItem>)}
          </SelectContent>
        </Select>
        {allServices.length > 0 && (
          <Select value={filterService} onValueChange={setFilterService}>
            <SelectTrigger className="bg-gray-800 border-gray-700 text-white h-8 text-xs w-[170px]" data-testid="cal-filter-service">
              <SelectValue placeholder="Servis" />
            </SelectTrigger>
            <SelectContent className="bg-gray-900 border-gray-700">
              <SelectItem value="all" className="text-white text-xs">Tüm Servisler</SelectItem>
              {allServices.map(s => <SelectItem key={s} value={s} className="text-white text-xs">{s}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        {/* Legend */}
        <div className="flex items-center gap-3 ml-auto text-[10px] text-gray-400">
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-500" />Gecikmiş</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-400" />Bugün Bitmeli</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-green-500" />Teslime Hazır</span>
          <span className="flex items-center gap-1"><span className="text-brand font-bold">T</span>Hedef Tarih</span>
        </div>
      </div>

      {/* Calendar Grid */}
      <div className="border border-gray-700 rounded-lg overflow-hidden">
        {/* Header */}
        <div className="grid grid-cols-7 bg-gray-800">
          {DAYS_TR.map(d => (
            <div key={d} className="text-center text-xs text-gray-400 font-medium py-2 border-b border-gray-700">{d}</div>
          ))}
        </div>
        {/* Days */}
        <div className="grid grid-cols-7">
          {calendarDays.map((day, i) => {
            const key = toDateStr(day);
            const dayJobs = jobsByDate[key] || [];
            const isToday = isSameDay(day, today);
            const isCurrentMonth = day.getMonth() === currentDate.getMonth();
            const isWeekend = day.getDay() === 0 || day.getDay() === 6;

            return (
              <div key={key} className={`border-b border-r border-gray-700/50 ${
                viewMode === 'week' ? 'min-h-[200px]' : 'min-h-[80px]'
              } ${!isCurrentMonth && viewMode === 'month' ? 'opacity-40' : ''} ${
                isWeekend ? 'bg-gray-900/50' : 'bg-gray-900/20'
              }`} data-testid={`cal-day-${key}`}>
                <div className={`text-right px-1.5 pt-1 text-xs ${
                  isToday ? 'font-bold' : 'text-gray-500'
                }`}>
                  {isToday ? (
                    <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-brand text-white text-xs font-bold">
                      {day.getDate()}
                    </span>
                  ) : day.getDate()}
                </div>
                <div className="px-1 pb-1">
                  {dayJobs.slice(0, viewMode === 'week' ? 10 : 3).map((j, idx) => (
                    <JobChip key={`${j.id}-${idx}`} job={j} />
                  ))}
                  {dayJobs.length > (viewMode === 'week' ? 10 : 3) && (
                    <p className="text-[9px] text-gray-500 text-center">+{dayJobs.length - (viewMode === 'week' ? 10 : 3)} daha</p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Summary bar */}
      <div className="flex gap-3 mt-3 text-xs text-gray-400">
        <span>Toplam: <b className="text-white">{filteredJobs.length}</b> iş</span>
        <span>Gecikmiş: <b className="text-red-400">
          {filteredJobs.filter(j => j.targetCompletionDate && parseDate(j.targetCompletionDate) < today && !['teslim_edildi','completed'].includes(j.status)).length}
        </b></span>
        <span>Bugün Bitmeli: <b className="text-amber-400">
          {filteredJobs.filter(j => j.targetCompletionDate === todayStr && !['teslim_edildi','completed'].includes(j.status)).length}
        </b></span>
      </div>

      {/* Job Detail Modal */}
      <Dialog open={!!selectedJob} onOpenChange={() => setSelectedJob(null)}>
        <DialogContent className="bg-gray-900 border-gray-700 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Car className="h-5 w-5 text-brand" />{selectedJob?.carPlate}
            </DialogTitle>
          </DialogHeader>
          {selectedJob && (
            <div className="space-y-3" data-testid="cal-job-detail">
              <div className="flex items-center gap-2 flex-wrap">
                <Badge className={`${getS(selectedJob.status).bg} text-white`}>{getS(selectedJob.status).label}</Badge>
                {selectedJob.targetCompletionDate && parseDate(selectedJob.targetCompletionDate) < today
                  && !['teslim_edildi','completed'].includes(selectedJob.status) && (
                  <Badge className="bg-red-600 text-white text-xs"><AlertTriangle className="h-3 w-3 mr-1" />Gecikmis</Badge>
                )}
                {selectedJob.canViewDetail === false && (
                  <Badge className="bg-gray-600 text-white text-xs">Kisitli Erisim</Badge>
                )}
              </div>
              {/* Basic info - visible to everyone */}
              <div className="grid grid-cols-2 gap-2">
                <div className="bg-gray-800 rounded-lg p-2.5">
                  <p className="text-gray-400 text-xs flex items-center gap-1"><User className="h-3 w-3" />Musteri</p>
                  <p className="text-white text-sm font-semibold">{selectedJob.customerName}</p>
                </div>
                <div className="bg-gray-800 rounded-lg p-2.5">
                  <p className="text-gray-400 text-xs flex items-center gap-1"><Car className="h-3 w-3" />Plaka</p>
                  <p className="text-brand text-sm font-bold">{selectedJob.carPlate}</p>
                </div>
                <div className="bg-gray-800 rounded-lg p-2.5">
                  <p className="text-gray-400 text-xs flex items-center gap-1"><CalIcon className="h-3 w-3" />Gelis</p>
                  <p className="text-white text-sm">{selectedJob.arrivalDate ? new Date(selectedJob.arrivalDate + 'T00:00').toLocaleDateString('tr-TR') : '-'}</p>
                </div>
                <div className="bg-gray-800 rounded-lg p-2.5">
                  <p className="text-gray-400 text-xs flex items-center gap-1"><Clock className="h-3 w-3" />Hedef Teslim</p>
                  <p className={`text-sm font-semibold ${
                    selectedJob.targetCompletionDate && parseDate(selectedJob.targetCompletionDate) < today
                    && !['teslim_edildi','completed'].includes(selectedJob.status) ? 'text-red-400' : 'text-white'
                  }`}>{selectedJob.targetCompletionDate ? new Date(selectedJob.targetCompletionDate + 'T00:00').toLocaleDateString('tr-TR') : '-'}</p>
                </div>
              </div>
              <div className="bg-gray-800 rounded-lg p-2.5">
                <p className="text-gray-400 text-xs flex items-center gap-1 mb-1"><Wrench className="h-3 w-3" />Servisler</p>
                <div className="flex flex-wrap gap-1">
                  {(selectedJob.services || []).map((s, i) => (
                    <Badge key={`cal-svc-${s}-${i}`} variant="outline" className="border-brand/50 text-brand text-xs">{s}</Badge>
                  ))}
                </div>
              </div>
              {/* Sensitive info - only if canViewDetail */}
              {selectedJob.canViewDetail !== false ? (
                <>
                  {(selectedJob.salesName || selectedJob.technicianName) && (
                    <div className="grid grid-cols-2 gap-2">
                      {selectedJob.salesName && (
                        <div className="bg-gray-800 rounded-lg p-2.5">
                          <p className="text-gray-400 text-xs">Satis</p>
                          <p className="text-white text-sm">{selectedJob.salesName}</p>
                        </div>
                      )}
                      {selectedJob.technicianName && (
                        <div className="bg-gray-800 rounded-lg p-2.5">
                          <p className="text-gray-400 text-xs">Uygulamaci</p>
                          <p className="text-white text-sm">{selectedJob.technicianName}</p>
                        </div>
                      )}
                    </div>
                  )}
                  <div className="bg-gray-800 rounded-lg p-2.5 flex justify-between items-center">
                    <div><p className="text-gray-400 text-xs">Tutar</p><p className="text-white font-bold">{selectedJob.totalPrice} TL</p></div>
                    <div className="text-right"><p className="text-gray-400 text-xs">Kalan</p><p className="text-red-400 font-bold">{selectedJob.remainingBalance || 0} TL</p></div>
                  </div>
                  {onEditJob && (
                    <Button size="sm" onClick={() => { onEditJob(selectedJob); setSelectedJob(null); }}
                      className="w-full bg-brand hover:bg-brand text-white" data-testid="cal-edit-job-btn">
                      Isi Duzenle
                    </Button>
                  )}
                </>
              ) : (
                <div className="bg-gray-800/50 rounded-lg p-4 text-center border border-gray-700">
                  <p className="text-gray-400 text-sm">Bu isin detaylarini gorme yetkiniz yok.</p>
                  <p className="text-gray-500 text-xs mt-1">Sadece atanmis personel, yonetici ve admin erisebilir.</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OperationalCalendar;
